import React, { useState, useEffect } from "react";
import { string, shape } from "prop-types";
import "./facebook-ad-preview.styles.scss";
import { generateNearlyUniqueID } from "common/uuid";
import replaceHtmlContent from "./helpers/replace-html-content";

const propTypes = {
  image: shape({
    image: string,
    type: string,
    mimeType: string
  }).isRequired,
  facebookPage: shape({
    name: string,
    picture: string
  }).isRequired,
  facebookNewsfeedData: shape({
    body: string,
    title: string,
    message: string,
    domain: string
  }).isRequired,
  width: string
};

const FacebookAdPreview = ({ image, facebookPage, facebookNewsfeedData, width = "270px" }) => {
  const [height, setHeight] = useState(100);
  const [isLoaded, setIsLoaded] = useState(false);

  const uuid = generateNearlyUniqueID();

  const resizeIframe = e => {
    try {
      const iframeElement = e.target;
      const targetHeight = iframeElement.contentWindow.document.body.scrollHeight;
      setHeight(targetHeight);
    } catch (error) {
      console.error(e);
    }
  };

  const resizeImage = e => {
    try {
      const imgElement = e.target;
      const targetHeight = imgElement.height;
      setHeight(targetHeight);
    } catch (error) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (!image.image) {
      setIsLoaded(false);
    }
  }, [image.image]);

  return (
    <table title="facebook-ad-preview" cellSpacing="0" cellPadding="0" style={{ width }}>
      <tbody>
        <tr>
          <td
            border="0"
            style={{
              width: "100%",
              backgroundColor: "#fff",
              fontFamily: "Helvetica,Arial,sans-serif",
              color: "#9197a3",
              padding: "10px 10px 12px",
              borderWidth: "1px",
              borderStyle: "solid",
              borderColor: "#bcbcbc"
            }}
          >
            <table
              id="fb-newsfeed-preview"
              border="0"
              cellSpacing="0"
              cellPadding="0"
              style={{
                width: "100%",
                fontFamily: "Helvetica,Arial,sans-serif",
                color: "#141823"
              }}
            >
              <tbody>
                {/* START - HEADER ROW */}
                <tr>
                  <td
                    style={{
                      width: "40px",
                      padding: "0px !important",
                      border: "none !important",
                      backgroundColor: "#ffffff"
                    }}
                  >
                    {facebookPage.picture ? (
                      <img
                        src={facebookPage.picture}
                        style={{
                          width: 40,
                          borderWidth: "1px",
                          borderStyle: "solid",
                          borderColor: "#e5e5e5"
                        }}
                        alt={replaceHtmlContent(facebookPage.name)}
                      />
                    ) : (
                      <div
                        style={{
                          width: "40px",
                          height: "40px",
                          backgroundColor: "#CCCCCC",
                          color: "#999999",
                          fontSize: "24px",
                          lineHeight: "40px",
                          textAlign: "center",
                          borderRadius: "50%"
                        }}
                      >
                        ?
                      </div>
                    )}
                  </td>
                  <td
                    style={{
                      paddingLeft: "8px",
                      border: "none !important",
                      backgroundColor: "#ffffff"
                    }}
                  >
                    <div
                      style={{
                        fontSize: "14px",
                        fontWeight: "700",
                        lineHeight: "17px",
                        textAlign: "left",
                        padding: "1px 0 3px"
                      }}
                    >
                      {replaceHtmlContent(facebookPage.name)}
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#9197a3",
                        lineHeight: "16px",
                        textAlign: "left"
                      }}
                    >
                      <span style={{ verticalAlign: "middle" }}>Sponsored</span>
                      <i className="fa fa-globe ml-1" aria-hidden="true" />
                    </div>
                  </td>
                </tr>
                {/* END - HEADER ROW */}

                {/* START - BODY ROW */}
                <tr>
                  <td
                    colSpan="2"
                    style={{
                      border: "none !important",
                      padding: "0px !important"
                    }}
                  >
                    <div
                      id="fb-newsfeed-body"
                      style={{
                        fontSize: "14px",
                        lineHeight: "18px",
                        fontWeight: 400,
                        color: "#141823",
                        padding: "8px 0"
                      }}
                    >
                      {replaceHtmlContent(facebookNewsfeedData.body) || ""}
                    </div>
                    <div
                      style={{
                        padding: "0px",
                        borderRadius: "4px",
                        boxShadow: "0px 1px 1px 0px rgba(0,0,0,0.1)"
                      }}
                    >
                      {/* START - MAIN IMAGE */}
                      <div>
                        <div
                          className="fb-preview-image"
                          style={{
                            border: "1px #dadada solid",
                            borderBottom: "none",
                            borderRadius: "4px 4px 0 0",
                            position: "relative"
                          }}
                        >
                          {image.mimeType === "text/html" ? (
                            <iframe
                              title="Preview Your Ad"
                              src={image.image}
                              style={{
                                width: "1px",
                                height,
                                minWidth: "100%",
                                borderRadius: "4px 4px 0 0"
                              }}
                              frameBorder="0"
                              scrolling="no"
                              data-testid="iframe"
                              onLoad={e => resizeIframe(e)}
                            />
                          ) : (
                            <>
                              <div style={{ position: "relative" }}>
                                <img
                                  src={image.image}
                                  alt={image.type}
                                  style={{
                                    borderRadius: "4px 4px 0 0",
                                    minHeight: "100px",
                                    zIndex: 2
                                  }}
                                  onLoad={e => {
                                    setIsLoaded(true);
                                    resizeImage(e);
                                  }}
                                />
                              </div>
                              <div
                                id={`placeholder-${uuid}`}
                                className="facebook-ad-preview-placeholder"
                                style={{ display: isLoaded ? "none" : "flex" }}
                              >
                                <div>
                                  <i className="fa fa-arrow-circle-o-up mr-1" /> Upload
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                      {/* END - MAIN IMAGE */}

                      {/* START - FOOTER */}
                      <div
                        style={{
                          padding: "8px",
                          border: "1px #e9eaed solid",
                          borderTop: "none",
                          borderLeftColor: "#dadada",
                          borderRightColor: "#dadada",
                          borderBottomColor: "#cccccc",
                          borderRadius: "0 0 4px 4px"
                        }}
                      >
                        <div
                          id="fb-newsfeed-title"
                          style={{
                            fontSize: "14px",
                            fontWeight: 700,
                            lineHeight: "18px"
                          }}
                        >
                          {replaceHtmlContent(facebookNewsfeedData.title) ||
                            "Your header text appears here"}
                        </div>
                        <div
                          id="fb-newsfeed-message"
                          style={{
                            fontSize: "12px",
                            lineHeight: "16px",
                            maxWidth: "260px"
                          }}
                        >
                          {replaceHtmlContent(facebookNewsfeedData.message)}
                        </div>
                        <div
                          id="fb-newsfeed-domain"
                          style={{
                            color: "#9197a3",
                            fontSize: "12px",
                            lineHeight: "16px",
                            marginTop: "4px",
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                            wordWrap: "break-word",
                            textOverflow: "ellipsis",
                            maxWidth: "260px"
                          }}
                        >
                          {replaceHtmlContent(facebookNewsfeedData.domain)?.toUpperCase() ||
                            "Your site domain appears here"}
                        </div>
                      </div>
                      {/* END - FOOTER */}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>
  );
};

FacebookAdPreview.propTypes = propTypes;

export default FacebookAdPreview;
